import React, { useEffect, useMemo, useState } from "react";
import {
  AccessTime,
  AssignmentLateOutlined,
  CheckCircleOutline,
  EventBusyOutlined,
  LocalHospitalOutlined,
  Refresh,
  SchoolOutlined,
  WarningOutlined,
} from "@material-ui/icons";
import { useParent } from "../../../../context/ParentContext";
import { baseUrl } from "../../../../services/config";
import "./parent-progress.css";

const SUMMARY_ITEMS = [
  {
    key: "total_absent",
    label: "Absent",
    icon: EventBusyOutlined,
    tone: "red",
  },
  {
    key: "total_sick",
    label: "Sick",
    icon: LocalHospitalOutlined,
    tone: "violet",
  },
  {
    key: "total_leave",
    label: "Leave",
    icon: AssignmentLateOutlined,
    tone: "blue",
  },
  {
    key: "total_late_coming",
    label: "Late Coming",
    icon: AccessTime,
    tone: "amber",
  },
  {
    key: "total_home_work_not_done",
    label: "Homework Not Done",
    icon: SchoolOutlined,
    tone: "orange",
  },
  {
    key: "total_improper_uniform",
    label: "Improper Uniform",
    icon: WarningOutlined,
    tone: "pink",
  },
  {
    key: "total_test_not_prepared",
    label: "Test Not Prepared",
    icon: AssignmentLateOutlined,
    tone: "slate",
  },
];

const EMPTY_PROGRESS = SUMMARY_ITEMS.reduce(
  (result, item) => ({ ...result, [item.key]: 0 }),
  { attendance: [] },
);

const getStudentId = (child) =>
  child?.student?.id || child?.student_id || child?.id || null;

function formatDate(value) {
  if (!value) return "Date unavailable";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-PK", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

function normalizePayload(result) {
  const payload = result?.payload || result || {};
  return {
    ...EMPTY_PROGRESS,
    ...payload,
    attendance: Array.isArray(payload.attendance) ? payload.attendance : [],
  };
}

export default function MonthlyStudentAttendance() {
  const { selectedChild } = useParent();
  const [progress, setProgress] = useState(EMPTY_PROGRESS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const studentId = getStudentId(selectedChild);
  const studentName = selectedChild?.student?.name || "Student";

  const loadProgress = async () => {
    if (!studentId) {
      setProgress(EMPTY_PROGRESS);
      setError("");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("parentToken");
      const response = await fetch(
        `${baseUrl}/parent-student-attendance/${studentId}`,
        {
          headers: {
            Accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        },
      );
      const result = await response.json();
      if (!response.ok)
        throw new Error(result?.message || "Unable to load progress data.");
      setProgress(normalizePayload(result));
    } catch (requestError) {
      setProgress(EMPTY_PROGRESS);
      setError(requestError.message || "Unable to load progress data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProgress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  const statusOptions = useMemo(
    () => [
      "all",
      ...Array.from(
        new Set(
          progress.attendance.map((item) => item.status_name).filter(Boolean),
        ),
      ),
    ],
    [progress.attendance],
  );

  const visibleAttendance = useMemo(() => {
    const records =
      filter === "all"
        ? progress.attendance
        : progress.attendance.filter((item) => item.status_name === filter);
    return [...records].sort((a, b) =>
      String(b.date).localeCompare(String(a.date)),
    );
  }, [filter, progress.attendance]);

  const totalIncidents = SUMMARY_ITEMS.reduce(
    (total, item) => total + Number(progress[item.key] || 0),
    0,
  );
  const unreadCount = progress.attendance.filter(
    (item) => Number(item.is_read) === 0,
  ).length;

  return (
    <section className="parent-progress-page">
      <div className="parent-progress-hero">
        <div>
          <span className="parent-progress-eyebrow">Student overview</span>
          <h1>Monthly Student Attendance</h1>
          <p>
            {selectedChild
              ? `A clear record of attendance and discipline updates for ${studentName}.`
              : "Select a child to view attendance and progress details."}
          </p>
        </div>
        <div
          className="parent-progress-hero-stat"
          aria-label={`${totalIncidents} total records`}
        >
          <span>{totalIncidents}</span>
          <small>Total records</small>
        </div>
      </div>

      {!studentId ? (
        <PageState
          icon={SchoolOutlined}
          title="No student selected"
          text="Please select a child from the top menu to view their progress."
        />
      ) : loading ? (
        <PageState
          loading
          title="Loading progress"
          text="We are getting the latest attendance records."
        />
      ) : error ? (
        <PageState
          icon={WarningOutlined}
          error
          title="Progress could not be loaded"
          text={error}
          onRetry={loadProgress}
        />
      ) : (
        <>
          <div
            className="parent-progress-summary"
            aria-label="Progress summary"
          >
            {SUMMARY_ITEMS.map(({ key, label, icon: Icon, tone }) => (
              <article
                className={`parent-progress-card tone-${tone}`}
                key={key}
              >
                <div className="parent-progress-card-icon">
                  <Icon />
                </div>
                <div>
                  <strong>{Number(progress[key] || 0)}</strong>
                  <span>{label}</span>
                </div>
              </article>
            ))}
          </div>

          <div className="parent-progress-history">
            <div className="parent-progress-history-header">
              <div>
                <div className="parent-progress-title-row">
                  <h2>Attendance history</h2>
                  {unreadCount > 0 && (
                    <span className="parent-progress-new-count">
                      {unreadCount} new
                    </span>
                  )}
                </div>
                <p>
                  {progress.attendance.length} record
                  {progress.attendance.length === 1 ? "" : "s"} available
                </p>
              </div>
              {statusOptions.length > 1 && (
                <label className="parent-progress-filter">
                  <span>Show</span>
                  <select
                    value={filter}
                    onChange={(event) => setFilter(event.target.value)}
                  >
                    {statusOptions.map((status) => (
                      <option value={status} key={status}>
                        {status === "all" ? "All updates" : status}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>

            {visibleAttendance.length ? (
              <div className="parent-progress-list">
                {visibleAttendance.map((record) => (
                  <article className="parent-progress-record" key={record.id}>
                    <div className="parent-progress-record-marker">
                      <CheckCircleOutline />
                    </div>
                    <div className="parent-progress-record-content">
                      <div>
                        <h3>{record.status_name || "Attendance update"}</h3>
                        <p>{formatDate(record.date)}</p>
                      </div>
                      {Number(record.is_read) === 0 ? (
                        <span className="parent-progress-unread">New</span>
                      ) : (
                        <span className="parent-progress-read">Viewed</span>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="parent-progress-empty">
                <CheckCircleOutline />
                <h3>No attendance updates</h3>
                <p>
                  {filter === "all"
                    ? "There are no records for this student yet."
                    : `There are no ${filter.toLowerCase()} records.`}
                </p>
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
}

function PageState({ icon: Icon, title, text, loading, error, onRetry }) {
  return (
    <div
      className={`parent-progress-state${
        error ? " parent-progress-error" : ""
      }`}
      role={error ? "alert" : "status"}
    >
      {loading ? (
        <span className="parent-progress-spinner" />
      ) : (
        Icon && <Icon />
      )}
      <h2>{title}</h2>
      <p>{text}</p>
      {onRetry && (
        <button type="button" onClick={onRetry}>
          <Refresh fontSize="small" /> Try again
        </button>
      )}
    </div>
  );
}
